import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@repo/ui";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import BackButton from "../../../../components/BackButton";
import {
  getWithdrawalById,
  ProductExchangeResponse,
  WithdrawResponse,
  ManualProfileResponse,
  getManualProfile,
} from "../../../../services/withdrawApiService";
import { getProductExchangeById } from "../../../../services/withdrawApiService";
import { getProduct, Product } from "../../../../services/productApiService";

import { Alert } from "@repo/ui";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { getCookie } from "../../../../utils/cookieUtils";

type ExtendedProduct = Product & { quantity: number };

const DonationReceiptStep1: React.FC = () => {
  const { withdrawId } = useParams<{ withdrawId: string }>();
  const navigate = useNavigate();

  const [withdrawal, setWithdrawal] = useState<WithdrawResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for products included in the donation (if any)
  const [products, setProducts] = useState<ExtendedProduct[]>([]);
  // State for full product exchange data (includes adminNotes)
  const [exchangeData, setExchangeData] =
    useState<ProductExchangeResponse | null>(null);
  const [manualProfile, setManualProfile] = useState<ManualProfileResponse | null>(null);
  const user = useSelector((state: RootState) => state.auth.user);

  // Early redirect if no auth token
  useEffect(() => {
    const jwt = getCookie("authToken");
    if (!jwt) {
      const current = encodeURIComponent(window.location.href);
      console.warn("[DonationReceiptStep1] No auth token, redirecting to login");
      window.location.href = `${import.meta.env.VITE_LANDING_APP_URL}/auth?redirect=${current}`;
    } else {
      console.info("[DonationReceiptStep1] Auth token present");
    }
  }, []);

  // Load withdrawal info when component mounts
  useEffect(() => {
    if (withdrawId) {
      console.info("[DonationReceiptStep1] Fetching withdrawal", withdrawId);
      getWithdrawalById(withdrawId)
        .then((data) => {
          console.info("[DonationReceiptStep1] Withdrawal raw data", data);
          if (user && data.userID !== user.id) {
            console.warn(
              "[DonationReceiptStep1] Withdrawal does not belong to user. withdrawal user:",
              data.userID,
              "current user:",
              user.id
            );
            setError("No puedes acceder a este retiro.");
            setLoading(false);
            return;
          }
          setWithdrawal(data);
          console.info(
            "[DonationReceiptStep1] Withdrawal loaded",
            data.id,
            "user:",
            data.userID,
            "payload:",
            data
          );
          setLoading(false);
        })
        .catch((err) => {
          console.error(
            "[DonationReceiptStep1] Error al recuperar el retiro",
            err
          );
          const status = (err as any)?.response?.status;
          if (status === 401) {
            setError("No autorizado");
            const current = encodeURIComponent(window.location.href);
            window.location.href = `${import.meta.env.VITE_LANDING_APP_URL}/auth?redirect=${current}`;
          } else {
            setError("Error al recuperar la información de tu retiro.");
          }
          setLoading(false);
        });
    }
  }, [withdrawId, user]);

  // If the withdrawal contains a productExchangeId, load its products and full exchange info.
  useEffect(() => {
    if (withdrawal && withdrawal.productExchangeId) {
      getProductExchangeById(withdrawal.productExchangeId)
        .then(async (exchange: ProductExchangeResponse) => {
          // Save the full exchange data (including adminNotes)
          setExchangeData(exchange);
          // Map over the new products array, which contains objects with productId and quantity.
          const fetchedProducts = await Promise.all(
            exchange.products.map(async (pq) => {
              try {
                const prod = await getProduct(pq.productId);
                // Attach quantity from the exchange object to the retrieved product.
                return { ...prod, quantity: pq.quantity };
              } catch (error) {
                console.error("Error fetching product:", error);
                return null;
              }
            })
          );
          setProducts(fetchedProducts.filter(Boolean) as ExtendedProduct[]);
        })
        .catch((err) => {
          console.error("Error fetching product exchange data:", err);
        });
    }
  }, [withdrawal]);

  // If unauthorized, redirect to login with return URL.
  useEffect(() => {
    if (error?.toLowerCase().includes("token") || error?.toLowerCase().includes("unauthorized")) {
      const current = encodeURIComponent(window.location.href);
      console.warn(
        "[DonationReceiptStep1] Redirecting to landing auth due to auth error"
      );
      window.location.href = `${import.meta.env.VITE_LANDING_APP_URL}/auth?redirect=${current}`;
    }
  }, [error]);

  useEffect(() => {
    if (withdrawal?.id) {
      getManualProfile(withdrawal.id)
        .then((profile) => {
          setManualProfile(profile);
        })
        .catch(() => setManualProfile(null));
    }
  }, [withdrawal]);

  const handleContinue = () => {
    console.info("[DonationReceiptStep1] Continuing to step2");
    // Navigate to step 2 of the process.
    navigate(`/my-table/sign-donation-receipt/${withdrawId}/step2`);
  };

  const handleBack = () => {
    navigate("/my-table");
  };

  // Define the steps for the journey as plain strings.
  const steps = ["Revisión", "Firma", "Confirmación"];

  if (loading) {
    return <div>Cargando...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!withdrawal) {
    return <div>No se encontró la información del retiro.</div>;
  }

  return (
    <PageContainer>
      <div className="mt-16"></div>
      <StepIndicator steps={steps} currentStep={1} progressPercentage={33} />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton text="Regresar" onClick={handleBack} />

        <h2 className="text-2xl font-bold mb-4 text-primary">
          Revisión de tu retiro
        </h2>
        <p className="text-gray-600 mb-4">
          Verifica que la siguiente información es correcta antes de proceder a
          firmar tu recibo de donación.
        </p>

        {/* Monto Solicitado */}
        <div className="bg-background p-4 mb-4">
          <div className="text-gray-700 text-sm">Monto solicitado:</div>
          <div className="text-gray-900 text-2xl font-bold">
            {withdrawal.amount.toLocaleString("es-MX", {
              style: "currency",
              currency: withdrawal.currency || "MXN",
            })}
          </div>
        </div>

        {/* Cuenta Bancaria (opcional) */}
        {manualProfile ? (
          <div className="bg-background p-4 mb-4">
            <div className="text-gray-700 text-sm">Cuenta bancaria:</div>
            <div className="text-gray-900 text-xl font-bold">
              {manualProfile.bank || ""}{" "}
              {manualProfile.clabe ? `- CLABE: ${manualProfile.clabe}` : ""}
            </div>
            {manualProfile.holderName && (
              <div className="text-gray-700 text-sm">
                Titular: {manualProfile.holderName}
              </div>
            )}
          </div>
        ) : null}

        {/* Fecha de solicitud (opcional) */}
        {withdrawal.requestedAt && (
          <div className="bg-background p-4 mb-4">
            <div className="text-gray-700 text-sm">Fecha de solicitud:</div>
            <div className="text-gray-900 text-xl font-bold">
              {new Date(withdrawal.requestedAt).toLocaleDateString("es-MX")}
            </div>
          </div>
        )}

        {/* Productos incluidos, si existen */}
        {products.length > 0 && (
          <div className="bg-background p-4 mb-4">
            <div className="text-gray-700 text-sm mb-2">
              El monto de la donación incluye estos productos:
            </div>
            <div className="grid grid-cols-2 gap-4">
              {products.map((product) => {
                // Look up the corresponding quantity from the exchange data using product.id.
                const exchangeItem = exchangeData?.products.find(
                  (item) => item.productId === product.id
                );
                return (
                  <div
                    key={product.id}
                    className="bg-white p-2 rounded shadow flex flex-col items-center"
                  >
                    <img
                      src={product.pictures[0]}
                      alt={product.title}
                      className="w-20 h-20 object-cover mb-2"
                    />
                    <div className="text-sm font-bold text-gray-900">
                      {product.title}
                    </div>
                    <div className="text-xs text-gray-700">
                      {product.price.toLocaleString("es-MX", {
                        style: "currency",
                        currency: withdrawal.currency || "MXN",
                      })}
                    </div>
                    {exchangeItem && (
                      <div className="text-xs text-gray-700">
                        {`Cantidad: ${exchangeItem.quantity}`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* If adminNotes exist in the exchange data, show them in an Alert with type "info" */}
        {exchangeData?.adminNotes && (
          <div className="mt-4">
            <Alert message={exchangeData.adminNotes} level="info" />
          </div>
        )}

        <Button
          label="Continuar"
          onClick={handleContinue}
          type="primary"
          className="w-full mt-6"
        />
      </AnimatedCard>
    </PageContainer>
  );
};

export default DonationReceiptStep1;
